import type { ChangeEvent } from 'react';
import { memo, useCallback, useState } from 'react';

import type { KcContext } from 'keycloakify/login/KcContext';

import { CheckCircle } from '@tripleten/icons/solid/check-circle.tsx';
import { Copy01 } from '@tripleten/icons/solid/copy01.tsx';

import { Form } from 'src/shared/components/form/form.js';
import { Input } from 'src/shared/components/input/input.js';
import { useGetMsg } from 'src/shared/utils/kc-messages.js';

import { cnLoginConfigTotp } from './login-config-totp.constants.js';

import './login-config-totp.css';

const otpRegex = /^\d{0,6}$/;

export interface LoginConfigTotpProps {
    kcContext: KcContext.LoginConfigTotp;
}

export const LoginConfigTotp = memo<LoginConfigTotpProps>(({ kcContext }) => {
    const { url, totp, mode, messagesPerField } = kcContext;

    const { msgStr } = useGetMsg(kcContext);

    const [otp, setOtp] = useState('');
    const onChangeHandler = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        if (!e.target) {
            return;
        }
        const value = e.target.value;
        if (!otpRegex.test(value)) {
            return;
        }
        setOtp(value);
    }, []);

    const [copied, setCopied] = useState(false);

    const copyCode = useCallback(async () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1000);
        await navigator.clipboard.writeText(totp.totpSecretEncoded);
    }, [totp.totpSecretEncoded]);

    const errorMessage = messagesPerField.existsError('totp') ? msgStr('loginTotpCodeError') : undefined;

    return (
        <Form action={url.loginAction} kcContext={kcContext} title={msgStr('loginTotpTitle')}>
            <div className={cnLoginConfigTotp('text-block')}>
                {mode && mode === 'manual' ? (
                    <div className={cnLoginConfigTotp('prompt')}>{msgStr('loginTotpSetupKey')}</div>
                ) : (
                    <div className={cnLoginConfigTotp('prompt')}>{msgStr('loginTotpSetupScan')}</div>
                )}
                <div className={cnLoginConfigTotp('note')}>{msgStr('loginTotpTip')}</div>
            </div>

            {mode && mode === 'manual' ? (
                <>
                    <div className={cnLoginConfigTotp('setup-code-block')}>
                        <span className={cnLoginConfigTotp('setup-code')}>{totp.totpSecretEncoded}</span>
                        <span className={cnLoginConfigTotp('separator')}></span>
                        {copied ? (
                            <button className={cnLoginConfigTotp('copy-button')} disabled type="button">
                                <CheckCircle size={24} className={cnLoginConfigTotp('copy-icon')} />
                                {msgStr('loginTotpSetupKeyCopied')}
                            </button>
                        ) : (
                            <button
                                className={cnLoginConfigTotp('copy-button')}
                                onClick={copyCode}
                                type="button"
                                tabIndex={0}
                            >
                                <Copy01 size={24} className={cnLoginConfigTotp('copy-icon')} />
                                {msgStr('loginTotpSetupKeyCopy')}
                            </button>
                        )}
                    </div>
                    <div className={cnLoginConfigTotp('text-block')}>
                        <a href={totp.qrUrl} className={cnLoginConfigTotp('action-link')} tabIndex={0}>
                            {msgStr('loginTotpScanPossible')}
                        </a>
                    </div>
                </>
            ) : (
                <>
                    <img
                        id="kc-totp-secret-qr-code"
                        className={cnLoginConfigTotp('qr-code')}
                        src={`data:image/png;base64, ${totp.totpSecretQrCode}`}
                        alt="Figure: Qr-code"
                    />

                    <div className={cnLoginConfigTotp('text-block')}>
                        <a href={totp.manualUrl} className={cnLoginConfigTotp('action-link')} tabIndex={0}>
                            {msgStr('loginTotpScanTrouble')}
                        </a>
                    </div>
                </>
            )}
            <div className={cnLoginConfigTotp('text-block')}>
                <div className={cnLoginConfigTotp('prompt-code')}>{msgStr('loginTotpEnterCode')}</div>
            </div>

            <Input
                type="text"
                maxLength={6}
                tabIndex={0}
                id="totp"
                name="totp"
                label={msgStr('loginTotpCode')}
                autoComplete="off"
                autoFocus
                value={otp}
                onChange={onChangeHandler}
                aria-invalid={Boolean(errorMessage)}
                errorMessage={errorMessage}
            />

            <input type="hidden" id="totpSecret" name="totpSecret" value={totp.totpSecret} />
            {mode && <input type="hidden" id="mode" value={mode} />}

            <Input tabIndex={0} type="submit" value={msgStr('loginTotpSubmit')} disabled={otp.length < 6} />
        </Form>
    );
});
